import { DistrictDto, UpdateDistrictDto, UpdateDistrictListDto } from '@families-link/shared';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { CoreModal, CoreModalProps } from '../../../_core/components/_ui/core-modal';
import { Control, Controller, FieldValues, useForm } from 'react-hook-form';
import {
	Autocomplete,
	AutocompleteRenderInputParams,
	Box,
	Chip,
	createFilterOptions,
	MenuItem,
	TextField,
	Typography,
} from '@mui/material';
import { AutocompleteTextField, CoreAutoComplete } from '../../../_core/components/_ui/core-autocomplete';
import { CoreOverflowTypographyTooltip } from '../../../_core/components/_ui/core-overflow-tooltip';
import { buttonModalWindowStyles, chipDeleteIconStyles, mainTitleModalWindowStyles } from '../../../_core/styles';
import { ChipCloseIcon } from '../../../_core/components/icons';
import { containerButtonStyles, errorTextStyles, leftButtonStyles } from '../create-form/create-form.styles';
import { CoreButton } from '../../../_core/components/_ui/core-button';
import { mergeSx } from 'merge-sx';
import { CoreTextField } from '../../../_core/components/_ui/core-textfield';

export type AttachLinguistLinkDistrictFormModalProps = CoreModalProps & {
	defaultValues: DistrictDto;
	districtList?: Array<{ _id: string; company: string }>;
	formError?: any;
	submitAction(values: UpdateDistrictDto): Promise<void>;
	closeModal(): void;
};

export const AttachLinguistLinkDistrictFormModal: FC<AttachLinguistLinkDistrictFormModalProps> = (props) => {
	const [t] = useTranslation();
	const { submitAction, closeModal, defaultValues, districtList, formError, ...modalProps } = props;

	const resolver = classValidatorResolver(UpdateDistrictDto);

	const {
		handleSubmit,
		getValues,
		setValue,
		watch,
		register,
		control,
		formState: { isDirty, isLoading, isSubmitting, isSubmitted, isValid },
	} = useForm({
		defaultValues: {
			...defaultValues,
		},
		resolver,
		mode: 'all',
	});

	const disableSubmit = !isValid && (isDirty || isSubmitted);
	const loadingSubmit = isLoading || isSubmitting;

	const handleFormSubmit = useCallback(
		async (values: any) => {
			await submitAction(values);
		},
		[submitAction]
	);

	const selectedId = watch('districtLinguistLinkId');
	const selectedDistrict = districtList?.find((d) => d._id === selectedId) ?? null;

	const filterOptions = createFilterOptions({
		stringify: (option: any) => `${option._id ?? ''} ${option.company ?? ''}`.toLowerCase(),
	});

	return (
		<CoreModal {...modalProps} bodySx={{ pt: 0 }} paperSx={{ width: '100%' }}>
			<Typography variant="h5" sx={mainTitleModalWindowStyles}>
				Link District to Linguist Link
			</Typography>
			<Box component="form" onSubmit={handleSubmit(handleFormSubmit)}>
				<Box sx={{ mb: 5 }}>
					<CoreAutoComplete
						name="districtLinguistLinkId"
						control={control as unknown as Control<FieldValues>}
						controlSx={{ width: '100%' }}
						options={districtList || []}
						multiple={false}
						value={selectedDistrict}
						onChange={(_, selected) => {
							setValue('districtLinguistLinkId', selected?._id ?? '');
						}}
						filterOptions={filterOptions}
						getOptionLabel={(option) => option?._id ?? option}
						isOptionEqualToValue={(option, value) =>
							typeof value === 'string' ? option._id === value : option._id === value?._id
						}
						renderOption={(props, option) => (
							<MenuItem {...props} key={option._id}>
								<Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
									<Typography sx={{ fontSize: '14px' }}>{option._id}</Typography>
									{option.company?.trim() && (
										<Typography sx={{ fontSize: '14px', color: '#9397A4' }}>({option.company})</Typography>
									)}
								</Box>
							</MenuItem>
						)}
						renderInput={(params) => (
							<AutocompleteTextField
								{...params}
								placeholder={!getValues('districtLinguistLinkId') ? t('common.filter-district') : ''}
								sx={{
									'& .MuiOutlinedInput-root .MuiAutocomplete-input': {
										padding: '4.5px 4px 5.5px 5px',
										minWidth: 0,
									},
									'& .MuiInputBase-input': {
										height: '100%',
										padding: '0',
									},
									'& .MuiOutlinedInput-root': {
										'&.Mui-focused fieldset': {
											borderColor: (theme) => theme.colors.blue,
										},
									},
								}}
							/>
						)}
					/>
				</Box>

				{formError && (
					<Typography sx={errorTextStyles}>
						{formError?.data?.message ? formError.data.message : t('errors.invalid-credentials-error')}
					</Typography>
				)}
				<Box sx={containerButtonStyles}>
					<CoreButton onClick={closeModal} variant="secondary" sx={mergeSx(buttonModalWindowStyles, leftButtonStyles)}>
						{t('survey-templates.forms.buttons.close-form')}
					</CoreButton>
					<CoreButton
						type="submit"
						contraModeStyleButton
						disabled={disableSubmit}
						loading={loadingSubmit}
						sx={buttonModalWindowStyles}
					>
						Link
					</CoreButton>
				</Box>
			</Box>
		</CoreModal>
	);
};
